import {
  Action,
  AionzSnackbar,
  AionzTooltip,
  ConfirmationModalService,
  EmptyContentComponent,
  LoadingService,
  ModalTypes
} from '@aionzdev/design-system';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, finalize, forkJoin, map, switchMap, tap } from 'rxjs';

import { LoadState, PurchaseRequestStatus } from '../../../enums';
import { QuotationMapData } from '../../../interfaces/quotation-map-data';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { BranchesTooltipComponent } from '../../atoms/branches-tooltip/branches-tooltip.component';
import { ApprovalReviewComponent } from '../../components/approval-review/approval-review.component';
import { QuotationMapBoardComponent } from '../../components/quotation-map-board/quotation-map-board.component';
import { QuotationMapBoardData } from '../../../interfaces/quotation-map-board-data';
import { PurchaseRequestActivityPanelComponent } from '../../components/purchase-request-activity-panel/purchase-request-activity-panel.component';
import { PurchaseRequestStatusComponent } from '../../atoms/purchase-request-status/purchase-request-status.component';
import {
  deliveryBranchesLabel,
  injectBranchCities,
  toDeliveryBranchesSummary
} from '../../utils/branch-label.util';
import {
  buildApprovalReview,
  selecaoDaAprovacao,
  supplierHeaderColors
} from '../../utils/approval-review.util';

/**
 * Detalhe da solicitação aguardando aprovação, em modo leitura: o mesmo quadro de revisão que
 * o comprador vê no mapa antes de mandar para aprovação (`aionz-approval-review`), com o botão
 * de aprovar no topo.
 */
@Component({
  selector: 'aionz-approval-purchase-request-detail',
  imports: [
    AionzTooltip,
    ApprovalReviewComponent,
    DatePipe,
    EmptyContentComponent,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatTabsModule,
    PurchaseRequestActivityPanelComponent,
    QuotationMapBoardComponent,
    PurchaseRequestStatusComponent
  ],
  templateUrl: './approval-purchase-request-detail.component.html',
  styleUrl: './approval-purchase-request-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovalPurchaseRequestDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly purchasingService = inject(AbstractPurchasingService);
  private readonly confirmationModal = inject(ConfirmationModalService);
  private readonly snackBar = inject(AionzSnackbar);
  private readonly loadingService = inject(LoadingService);

  protected readonly LoadState = LoadState;
  protected readonly status = PurchaseRequestStatus.AWAITING_FOR_APPROVAL;
  protected readonly branchesTooltipComponent = BranchesTooltipComponent;
  private readonly locale = inject(LOCALE_ID);

  protected readonly loadState = signal<LoadState>(LoadState.IDLE);
  protected readonly approving = signal(false);
  protected readonly data = signal<QuotationMapData | null>(null);
  /** Cotações aguardando aprovação de cada item (orderId → ids das cotações). */
  private readonly winners = signal<Record<string, string[]>>({});
  private purchaseId = '';
  /** Id da solicitação para o painel de atividade (histórico e comentários). */
  protected readonly purchaseIdAtual = signal('');
  protected readonly activityPanel = viewChild.required(PurchaseRequestActivityPanelComponent);
  /** Voltar leva para onde a pessoa estava (o quadro com os filtros dela), quando informado. */
  private readonly returnUrl: string | undefined = this.router.currentNavigation()?.extras.state?.[
    'returnUrl'
  ];

  /**
   * As cotações aguardando aprovação convertidas na seleção do comprador: a mesma base do
   * resumo e do mapa. Item sem cotação aguardando aprovação fica sem seleção.
   */
  private readonly fonteAprovacao = computed(() => {
    const data = this.data();
    if (!data) return null;
    return selecaoDaAprovacao(
      {
        purchaseId: String(data.id ?? this.purchaseId),
        items: data.items ?? [],
        supplierIds: data.supplierIds ?? [],
        supplierFreights: data.supplierFreights
      },
      this.winners()
    );
  });

  /** O que vai ser aprovado, montado como na revisão do mapa. */
  protected readonly review = computed(() => {
    const fonte = this.fonteAprovacao();
    if (!fonte) return null;
    return buildApprovalReview(fonte, {
      accentColor: index => supplierHeaderColors(index).bar,
      locale: this.locale
    });
  });

  /** Dados do mapa de cotação só para leitura (aba "Mapa de cotação"). */
  protected readonly mapaLeitura = computed<QuotationMapBoardData | null>(() => {
    const fonte = this.fonteAprovacao();
    const review = this.review();
    if (!fonte || !review) return null;
    return {
      purchaseId: fonte.purchaseId,
      totalSelected: review.selectedItemsCount,
      totalValue: review.totalValue,
      items: fonte.items,
      supplierIds: fonte.supplierIds,
      supplierFreights: fonte.supplierFreights ?? new Map()
    };
  });

  /** Só há o que aprovar quando algum item tem cotação aguardando aprovação. */
  protected readonly canApprove = computed(
    () =>
      this.loadState() === LoadState.LOADED &&
      (this.review()?.suppliers.length ?? 0) > 0 &&
      !this.approving()
  );

  private readonly branchCities = injectBranchCities();
  protected readonly deliveryBranchesSummary = computed(() =>
    toDeliveryBranchesSummary(this.data()?.items ?? [], this.branchCities())
  );
  protected readonly deliveryBranchesLabel = computed(() =>
    deliveryBranchesLabel(this.deliveryBranchesSummary())
  );

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map(params => params.get('purchaseId') ?? ''),
        distinctUntilChanged(),
        tap(purchaseId => {
          this.purchaseId = purchaseId;
          this.purchaseIdAtual.set(purchaseId);
          this.loadState.set(LoadState.LOADING);
        }),
        switchMap(purchaseId =>
          forkJoin({
            map: this.purchasingService.getQuotationMapBoardData(purchaseId),
            winners: this.purchasingService.findApprovalWinners(purchaseId)
          })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ map: response, winners }) => {
          this.data.set(response.body ?? null);
          this.winners.set(winners);
          this.loadState.set(response.body ? LoadState.LOADED : LoadState.ERROR);
        },
        error: () => this.loadState.set(LoadState.ERROR)
      });
  }

  protected handleGoBack(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    this.router.navigate(['../../quadro'], { relativeTo: this.route });
  }

  protected openActivityTab(): void {
    this.activityPanel().openTab(0);
  }

  protected openCommentsTab(): void {
    this.activityPanel().openTab(1);
  }

  protected approve(): void {
    if (!this.canApprove()) return;

    this.confirmationModal
      .open(
        {
          headerTitle: 'Aprovar solicitação',
          title: 'Deseja aprovar esta solicitação?',
          description:
            'Ao confirmar, a compra segue para os fornecedores selecionados e a solicitação vai para as cotações concluídas.',
          labelConfirm: 'Aprovar',
          labelCancel: 'Cancelar',
          icon: { name: 'task_alt' }
        },
        ModalTypes.SUCCESS
      )
      .subscribe(action => {
        if (action !== Action.CONFIRM) return;

        this.approving.set(true);
        this.loadingService.showLoading();
        this.purchasingService
          .acceptPurchaseRequestOffers(this.purchaseId)
          .pipe(
            finalize(() => {
              this.approving.set(false);
              this.loadingService.hideLoading();
            })
          )
          .subscribe({
            next: () => {
              this.snackBar.success('Solicitação aprovada com sucesso!');
              this.handleGoBack();
            },
            error: () => this.snackBar.error('Erro ao aprovar solicitação.')
          });
      });
  }
}
